import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FaBriefcase, FaBuilding, FaCheckCircle, FaMapMarkerAlt, FaSearch, FaTools, FaUsers } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';
import { API_URL } from '../services/api';
import api from '../services/api';
import { getJobLocationLabel } from '../utils/jobLocationPresentation';
import { formatCompactJobPay } from '../utils/jobPayPresentation';
import {
  FEATURED_HIRE_ROLE_SLUGS,
  HIRE_ROLES,
  MARKET_DATA,
  VENDOR_SPECIALTIES,
  getJobQuery,
  getMarket,
  getVendorQuery,
} from '../data/seoLandingData';

interface VendorCard {
  id: number;
  slug: string | null;
  name: string;
  city: string;
  state: string;
  specialty: string;
  description: string | null;
  is_claimed: boolean;
}

interface JobCard {
  id: number;
  title: string;
  company: string;
  location: string | null;
  source: string;
  rate?: string | null;
  rate_min?: number | null;
  rate_max?: number | null;
  rate_type?: string | null;
}

function vendorPath(vendor: VendorCard) {
  return `/vendors/${vendor.slug || vendor.id}`;
}

function rateText(job: JobCard) {
  return formatCompactJobPay(job);
}

const MarketPage: React.FC = () => {
  const { city } = useParams<{ city: string }>();
  const market = getMarket(city);
  const marketSlug = market?.slug || '';
  const [vendors, setVendors] = useState<VendorCard[]>([]);
  const [jobs, setJobs] = useState<JobCard[]>([]);

  useEffect(() => {
    if (!market) return;

    fetch(`${API_URL}/api/vendors?${getVendorQuery(market, undefined, 6)}`)
      .then(r => r.json())
      .then(data => setVendors(data.vendors || []))
      .catch(() => setVendors([]));

    api.get(`/scraped-jobs?${getJobQuery(market, undefined, 6)}`)
      .then((res: any) => {
        const data = res.data?.jobs ?? res.data ?? [];
        setJobs(Array.isArray(data) ? data : []);
      })
      .catch(() => setJobs([]));
  }, [market, marketSlug]);

  if (!market) {
    return <Navigate to="/jobs" replace />;
  }

  const pageUrl = `https://stagecrew.app/markets/${market.slug}`;
  const title = `${market.name} Event Crew, AV Companies & Gigs | StageCrew`;
  const cityRoleSlugs = market.topRoles.length > 0 ? market.topRoles : FEATURED_HIRE_ROLE_SLUGS;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title={title}
        description={`${market.summary} Find local AV vendors, event crew, stagehand gigs, and hiring options in ${market.name}, ${market.stateAbbr}.`}
        url={pageUrl}
        keywords={`${market.name} AV companies, ${market.name} stagehands, hire event crew ${market.name}, ${market.name} stagehand jobs, ${market.name} event production`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: title,
          description: market.summary,
          url: pageUrl,
          about: ['Event crew', 'AV companies', 'Stagehand jobs', 'Live event production'],
          provider: { '@type': 'Organization', name: 'StageCrew', url: 'https://stagecrew.app' },
        }}
      />

      <section className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-4">
            <Link to="/" className="hover:text-blue-700">StageCrew</Link>
            <span>/</span>
            <span>{market.name}</span>
          </div>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700 mb-4">
              <FaMapMarkerAlt /> {market.name}, {market.stateAbbr}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-950 tracking-normal mb-5">
              {market.name} event crew, AV companies, and live event gigs
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl">{market.summary}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to={`/hire/${cityRoleSlugs[0]}/${market.slug}`} className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-3 font-semibold text-white hover:bg-blue-800">
                <FaUsers /> Hire local crew
              </Link>
              <Link to={`/vendors/city/${market.slug}`} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 hover:border-blue-400 hover:text-blue-700">
                <FaBuilding /> Browse vendors
              </Link>
              <Link to={`/gigs/${market.slug}`} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-800 hover:border-blue-400 hover:text-blue-700">
                <FaBriefcase /> Find gigs
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10 grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaSearch className="text-blue-700 mb-3" />
          <h2 className="text-lg font-bold text-slate-950 mb-2">For event buyers</h2>
          <p className="text-sm text-slate-600">{market.buyerSummary}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaBriefcase className="text-emerald-700 mb-3" />
          <h2 className="text-lg font-bold text-slate-950 mb-2">For workers</h2>
          <p className="text-sm text-slate-600">{market.workerSummary}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <FaCheckCircle className="text-amber-600 mb-3" />
          <h2 className="text-lg font-bold text-slate-950 mb-2">Local signal</h2>
          <p className="text-sm text-slate-600">{market.localInsight}</p>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-10 grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold text-slate-950 mb-4">Hire crew in {market.name}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {cityRoleSlugs.map(roleSlug => {
                const role = HIRE_ROLES[roleSlug];
                if (!role) return null;
                return (
                  <Link key={role.slug} to={`/hire/${role.slug}/${market.slug}`} className="rounded-lg border border-slate-200 p-4 hover:border-blue-400 hover:bg-blue-50 transition">
                    <div className="font-semibold text-slate-950">Hire {role.pluralLabel}</div>
                    <div className="text-sm text-slate-500 mt-1">{role.dayRate}</div>
                  </Link>
                );
              })}
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-950 mb-4">Find companies by service</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {market.topSpecialties.map(specialtySlug => {
                const specialty = VENDOR_SPECIALTIES[specialtySlug];
                if (!specialty) return null;
                return (
                  <Link key={specialty.slug} to={`/vendors/city/${market.slug}/${specialty.slug}`} className="rounded-lg border border-slate-200 p-4 hover:border-blue-400 hover:bg-blue-50 transition">
                    <div className="font-semibold text-slate-950">{specialty.pluralLabel}</div>
                    <div className="text-sm text-slate-500 mt-1">{specialty.buyerIntent}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-10 grid gap-8 lg:grid-cols-2">
        <div>
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-2xl font-bold text-slate-950">Featured vendors</h2>
            <Link to={`/vendors/city/${market.slug}`} className="text-sm font-semibold text-blue-700 hover:underline">View all</Link>
          </div>
          <div className="space-y-3">
            {vendors.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
                Vendor listings are being expanded for {market.name}.
              </div>
            ) : vendors.map(vendor => (
              <Link key={vendor.id} to={vendorPath(vendor)} className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-blue-400 hover:shadow-sm transition">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold text-slate-950">{vendor.name}</div>
                    <div className="text-sm text-slate-500">{vendor.city}, {vendor.state}</div>
                  </div>
                  <span className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                    {VENDOR_SPECIALTIES[Object.values(VENDOR_SPECIALTIES).find(s => s.apiValue === vendor.specialty)?.slug || 'production']?.label || 'Vendor'}
                  </span>
                </div>
                {vendor.description && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{vendor.description}</p>}
              </Link>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-2xl font-bold text-slate-950">Recent sourced listings</h2>
            <Link to={`/gigs/${market.slug}`} className="text-sm font-semibold text-blue-700 hover:underline">View all</Link>
          </div>
          <div className="space-y-3">
            {jobs.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
                Listings update as jobs are imported for {market.name}.
              </div>
            ) : jobs.map(job => (
              <Link key={job.id} to={`/jobs/${job.id}`} className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-blue-400 hover:shadow-sm transition">
                <div className="font-semibold text-slate-950">{job.title}</div>
                <div className="text-sm text-slate-500">
                  {job.company} - {getJobLocationLabel(job)}
                </div>
                <div className="text-sm font-semibold text-emerald-700 mt-2">{rateText(job)}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid gap-8 lg:grid-cols-3">
          <div>
            <FaTools className="text-blue-300 mb-3" />
            <h2 className="text-2xl font-bold mb-3">Claim or improve a listing</h2>
            <p className="text-slate-300 text-sm">Businesses can claim a vendor page, clean up details, and show buyers that the profile is maintained.</p>
            <Link to="/claim/vendor-listing" className="inline-flex mt-5 rounded-lg bg-white px-4 py-2 font-semibold text-slate-950 hover:bg-blue-50">
              Claim a listing
            </Link>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
            {market.nearbyMarkets.map(slug => {
              const nearby = MARKET_DATA[slug];
              if (!nearby) return null;
              return (
                <Link key={slug} to={`/markets/${slug}`} className="rounded-lg border border-slate-700 p-4 hover:border-blue-300 transition">
                  <div className="font-semibold">{nearby.name}</div>
                  <div className="text-sm text-slate-400 mt-1">{nearby.stateAbbr} market guide</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarketPage;
